import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { api } from "@/lib/api";
import { Lock, ShieldCheck, ShieldHalf, Zap, Store, Users } from "lucide-react";
import { toast } from "sonner";

import { LogoTile, TrustChip, StageHeader } from "./login/LoginShared";
import LoginForm from "./login/LoginForm";
import TwoFactorAuth from "./login/TwoFactorAuth";
import PasswordReset from "./login/PasswordReset";

// Premium-fintech split-screen login orchestrator. Owns the full auth state
// machine (password / 2fa_setup / 2fa_verify / forgot) and the role tabs
// (Admin / Partner / Merchant); delegates stage rendering to dedicated
// sub-components in ./login/. All data-testids preserved from the
// pre-refactor monolith so automation continues to pass.

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/466404e4-c296-49b3-be1d-22fda1002ea4/images/59b1323bcba3308405e02677ab514668890ddb191b4bb8f10dad74206abff7c6.png";

const ROLE_TABS = [
  { id: "admin",    label: "Admin",    icon: ShieldCheck, tagline: "Full commission OS access" },
  { id: "partner",  label: "Partner",  icon: Users,       tagline: "See your merchants & payouts" },
  { id: "merchant", label: "Merchant", icon: Store,       tagline: "See your volumes & PG fees" },
];

export default function Login() {
  const { login, completeTwoFA, demoLogin } = useAuth();
  const { app_name, logo_data_url } = useBranding();
  const [role, setRole] = useState("admin");
  const [stage, setStage] = useState("password"); // password | 2fa_setup | 2fa_verify | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [twofa, setTwofa] = useState({ token: "", qr: "", secret: "", uri: "" });
  const [code, setCode] = useState("");
  const codeRef = useRef(null);

  useEffect(() => {
    if ((stage === "2fa_verify" || stage === "2fa_setup") && codeRef.current) codeRef.current.focus();
  }, [stage]);

  // Switching role resets stage; admin runs 2FA, partner/merchant skip it.
  useEffect(() => {
    setStage("password");
    setCode("");
  }, [role]);

  const onLogin = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) { toast.error("Email and password required"); return; }
    setBusy(true);
    try {
      const r = await login(email.trim().toLowerCase(), password, role);
      if (r.stage === "signed_in") {
        toast.success("Signed in");
      } else if (r.stage === "2fa_setup") {
        setTwofa({ token: r.twofa_token, qr: r.qr_data_url, secret: r.secret, uri: r.provisioning_uri });
        setStage("2fa_setup");
      } else {
        setTwofa({ token: r.twofa_token, qr: "", secret: "", uri: "" });
        setStage("2fa_verify");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend sends 403 with messages like:
      //   "This account is registered as partner, not admin. Use the Partner tab."
      const msg = typeof detail === "string" ? detail : "Login failed";
      toast.error(msg);
    }
    setBusy(false);
  };

  const onVerify = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(code)) { toast.error("Enter the 6-digit code from your authenticator"); return; }
    setBusy(true);
    try {
      await completeTwoFA(twofa.token, code, stage === "2fa_setup");
      toast.success("Signed in");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Long, multi-tip error from the backend renders out of the default
      // toast height — give it more room when it overflows.
      const msg = typeof detail === "string" ? detail : "Verification failed";
      const isLong = msg.length > 60;
      toast.error(msg, isLong ? { duration: 9000, style: { maxWidth: "440px" } } : undefined);
      setCode("");
    }
    setBusy(false);
  };

  const onForgot = async (e) => {
    e?.preventDefault?.();
    if (!email) { toast.error("Enter your email first"); return; }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success("If that email exists, a reset link was sent");
      setStage("password");
    } catch (_err) {
      toast.error("Could not send reset email");
    }
    setBusy(false);
  };

  const onDemo = async () => {
    setDemoBusy(true);
    try {
      // Pass the active role so partner/merchant tabs sign into the
      // matching portal demo instead of the admin sandbox.
      await demoLogin(role);
      toast.success(
        role === "partner" ? "Entered partner demo" :
        role === "merchant" ? "Entered merchant demo" :
        "Entered demo sandbox — explore freely"
      );
    } catch (_err) {
      toast.error("Demo sign-in failed");
      setDemoBusy(false);
    }
  };

  const brandHead = (app_name || "BizAssist Online").trim().split(" ")[0];

  return (
    <div
      className="min-h-screen w-full bg-[var(--bg)] grid lg:grid-cols-[1.1fr_1fr]"
      data-testid="login-page"
    >
      {/* LEFT — Brand hero (desktop only) */}
      <aside
        className="relative hidden lg:flex flex-col justify-between overflow-hidden border-r border-[var(--line)]"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--ink)]/[0.04] via-transparent to-[var(--accent)]/[0.08] pointer-events-none" />
        <header className="relative z-10 px-12 pt-10 flex items-center gap-3">
          <LogoTile logo_data_url={logo_data_url} alt={brandHead} />
          <div>
            <div className="font-display text-[17px] tracking-tight leading-none text-[var(--ink)]">
              {brandHead}<span className="text-[var(--accent)]">.</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted)] mt-1.5">
              Commission OS
            </div>
          </div>
        </header>

        <div className="relative z-10 px-12 pb-16 max-w-xl space-y-7">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] border border-[var(--line-2)] px-3 py-1.5 rounded-full bg-[var(--surface)]/70 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
            Trusted by payment-gateway partners
          </div>
          <h1 className="font-display font-medium text-[44px] leading-[1.05] tracking-tight text-[var(--ink)]">
            Commission ledgers, <span className="italic text-[var(--accent)]">simplified</span>.
          </h1>
          <p className="text-[14.5px] leading-relaxed text-[var(--muted)] max-w-md">
            Reconcile partner payouts, PG earnings, and monthly P&amp;L —
            all from one keyboard-first dashboard.
          </p>

          <div className="grid grid-cols-3 gap-3 pt-2">
            <TrustChip icon={ShieldHalf} label="GST-compliant" />
            <TrustChip icon={Lock} label="256-bit TLS" />
            <TrustChip icon={Zap} label="Realtime sync" />
          </div>

          <div className="pt-6 border-t border-[var(--line)] text-[11px] uppercase tracking-[0.22em] text-[var(--muted)] flex items-center gap-3">
            <span>SOC 2 · ISO 27001 · PCI-DSS</span>
          </div>
        </div>
      </aside>

      {/* RIGHT — Form container */}
      <main className="flex items-center justify-center px-5 sm:px-10 py-10">
        <div className="w-full max-w-md">
          {/* Mobile-only compact brand header */}
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <LogoTile logo_data_url={logo_data_url} alt={brandHead} small />
            <div>
              <div className="font-display text-[17px] tracking-tight leading-none text-[var(--ink)]">
                {brandHead}<span className="text-[var(--accent)]">.</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted)] mt-1">
                Commission OS
              </div>
            </div>
          </div>

          <StageHeader stage={stage} role={role} />

          {/* Role picker — always visible. Single domain → user picks role explicitly. */}
          <div
            className="mt-6 grid grid-cols-3 gap-1.5 p-1 bg-[var(--surface-2)] border border-[var(--line)] rounded-sm"
            role="tablist"
            data-testid="login-role-tabs"
          >
            {ROLE_TABS.map((r) => {
              const Icon = r.icon;
              const active = role === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`login-role-${r.id}`}
                  onClick={() => setRole(r.id)}
                  className={`inline-flex items-center justify-center gap-1.5 h-9 px-2 text-[12px] font-medium rounded-sm transition-all
                    ${active
                      ? "bg-[var(--surface)] text-[var(--ink)] shadow-sm border border-[var(--line-2)]"
                      : "text-[var(--muted)] hover:text-[var(--ink)] border border-transparent"}`}
                >
                  <Icon size={13} strokeWidth={1.9} />
                  <span>{r.label}</span>
                </button>
              );
            })}
          </div>

          <div
            key={`${role}-${stage}`} /* remount triggers fade on tab / stage change */
            className="mt-6 animate-[fadeInUp_280ms_ease-out]"
          >
            {stage === "password" && (
              <LoginForm
                role={role}
                email={email} setEmail={setEmail}
                password={password} setPassword={setPassword}
                showPw={showPw} setShowPw={setShowPw}
                busy={busy} demoBusy={demoBusy}
                onLogin={onLogin}
                onDemo={onDemo}
                onForgot={() => setStage("forgot")}
              />
            )}

            {(stage === "2fa_setup" || stage === "2fa_verify") && (
              <TwoFactorAuth
                stage={stage}
                twofa={twofa}
                code={code} setCode={setCode}
                busy={busy}
                onVerify={onVerify}
                onBack={() => { setStage("password"); setCode(""); }}
                codeRef={codeRef}
              />
            )}

            {stage === "forgot" && (
              <PasswordReset
                email={email} setEmail={setEmail}
                busy={busy}
                onSubmit={onForgot}
                onBack={() => setStage("password")}
              />
            )}
          </div>

          <footer className="mt-10 pt-6 border-t border-[var(--line)] flex flex-col gap-3 text-[11px] text-[var(--muted)]">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="login-policy-links">
              <a
                href="https://www.bizassist.online/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[var(--ink)] transition-colors"
                data-testid="policy-privacy"
              >Privacy Policy</a>
              <span className="h-3 w-px bg-[var(--line)]" />
              <a
                href="https://www.bizassist.online/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[var(--ink)] transition-colors"
                data-testid="policy-terms"
              >Terms &amp; Conditions</a>
              <span className="h-3 w-px bg-[var(--line)]" />
              <a
                href="https://www.bizassist.online/refund-and-cancellation"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[var(--ink)] transition-colors"
                data-testid="policy-refund"
              >Refund &amp; Cancellation</a>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>© {new Date().getFullYear()} {brandHead}. All rights reserved.</div>
              <div className="inline-flex items-center gap-1.5">
                <ShieldCheck size={11} className="text-[var(--accent)]" />
                Protected by industry-grade encryption
              </div>
            </div>
          </footer>
        </div>
      </main>

      {/* Keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
